// VRT product form controller. One <vrt-product-form> wraps the option pickers,
// quantity stepper and add-to-cart button on the product page. It resolves the
// selected option values to a variant (from embedded JSON), then keeps the live
// price, availability, stock note, gallery image, ?variant URL and the hidden
// id/quantity inputs in sync. The actual add-to-cart + drawer is owned by
// vrt-cart.js — this component only keeps the button's data-variant-id /
// data-quantity current, so a single add path stays in one place.
//
// Money is never formatted here: per-variant price strings are pre-rendered in
// Liquid and embedded, and this component only swaps text.
//
// Stock label has 3 states: this.tagMto (product tagged `made-to-order`,
// constant) always wins; else a variant's own `mto` flag (continue-selling +
// zero real inventory — Shopify's `available` is already true here, so
// without this it would misleadingly read "In stock"); else the normal
// available/sold-out split.

class VrtProductForm extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('[data-vrt-product-data]');
    try {
      this.data = JSON.parse(dataEl.textContent);
    } catch (e) {
      this.data = { variants: [] };
    }

    this.form = this.querySelector('form');
    this.optionInputs = Array.from(this.querySelectorAll('[data-vrt-opt]'));
    this.idInput = this.querySelector('[data-vrt-variant-id]');
    this.qtyInput = this.querySelector('[data-vrt-qty-input]');
    this.qtyDisplay = this.querySelector('[data-vrt-qty-display]');
    this.addBtn = this.querySelector('[data-vrt-quick-add]');

    // Price/stock markup sits above <vrt-product-form> in the same PDP
    // details column, not inside it — this.querySelector alone would never
    // find them (real pre-existing bug: price/stock silently never updated
    // on variant switch). Scope from the PDP root instead, same pattern
    // already used for the gallery lookup below.
    const pdpRoot = this.closest('[data-vrt-pdp]') || this;
    this.priceEl = pdpRoot.querySelector('[data-vrt-price]');
    this.compareEl = pdpRoot.querySelector('[data-vrt-compare]');
    this.stockEl = pdpRoot.querySelector('[data-vrt-stock-label]');
    this.stockDot = pdpRoot.querySelector('[data-vrt-stock-dot]');
    this.shipsEl = pdpRoot.querySelector('[data-vrt-ships]');
    this.tagMto = this.dataset.tagMto === 'true';

    this.optionInputs.forEach((input) => {
      input.addEventListener('change', () => this.onOptionChange());
    });

    this.addEventListener('click', (e) => {
      const step = e.target.closest('[data-vrt-qty-step]');
      if (step) this.step(Number(step.dataset.vrtQtyStep));
    });

    this.gallery = this.closest('[data-vrt-pdp]')?.querySelector('[data-vrt-gallery]');
    if (this.gallery) {
      this.gallery.addEventListener('click', (e) => {
        const thumb = e.target.closest('[data-vrt-thumb]');
        if (thumb) {
          e.preventDefault();
          this.showMedia(thumb.dataset.mediaId);
          return;
        }
        if (e.target.closest('[data-vrt-zoom-open]') || e.target.closest('[data-vrt-stage]')) {
          this.openZoom();
        }
      });
    }

    this.zoomOverlay = pdpRoot.querySelector('[data-vrt-zoom-overlay]');
    if (this.zoomOverlay) {
      this.zoomOverlay.addEventListener('click', (e) => {
        if (e.target.closest('[data-vrt-zoom-close]') || e.target === this.zoomOverlay) {
          this.closeZoom();
        } else if (e.target.closest('[data-vrt-zoom-prev]')) {
          this.stepZoom(-1);
        } else if (e.target.closest('[data-vrt-zoom-next]')) {
          this.stepZoom(1);
        }
      });

      this.zoomOverlay.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.closeZoom();
        if (e.key === 'ArrowLeft') this.stepZoom(-1);
        if (e.key === 'ArrowRight') this.stepZoom(1);
      });
    }

    // Single/default-variant products have no option inputs — sync once so the
    // button/price reflect the embedded current variant.
    this.update();
  }

  get selectedOptions() {
    // One checked radio per option fieldset, ordered by option position.
    const byPos = [];
    this.optionInputs.forEach((input) => {
      if (input.checked) byPos[Number(input.dataset.position) - 1] = input.value;
    });
    return byPos;
  }

  findVariant() {
    const variants = this.data.variants || [];
    if (this.optionInputs.length === 0) return variants[0] || null;
    const sel = this.selectedOptions;
    return (
      variants.find(
        (v) => v.options.length === sel.length && v.options.every((o, i) => o === sel[i])
      ) || null
    );
  }

  onOptionChange() {
    this.update();
    const v = this.currentVariant;
    if (v && v.media != null) this.showMedia(String(v.media));
  }

  update() {
    const v = this.findVariant();
    this.currentVariant = v;

    if (this.priceEl && v) this.priceEl.textContent = v.price;
    if (this.compareEl) {
      if (v && v.compare) {
        this.compareEl.textContent = v.compare;
        this.compareEl.hidden = false;
      } else {
        this.compareEl.hidden = true;
      }
    }

    const available = !!(v && v.available);
    if (this.addBtn) {
      if (!v) {
        this.addBtn.disabled = true;
        this.addBtn.textContent = this.addBtn.dataset.labelUnavailable || 'Unavailable';
        this.addBtn.removeAttribute('data-variant-id');
      } else if (!available) {
        this.addBtn.disabled = true;
        this.addBtn.textContent = this.addBtn.dataset.labelSoldout || 'Sold out';
        this.addBtn.dataset.variantId = v.id;
      } else {
        this.addBtn.disabled = false;
        const base = this.addBtn.dataset.labelAdd || 'Add to cart';
        this.addBtn.textContent = v.price ? `${base} · ${v.price}` : base;
        this.addBtn.dataset.variantId = v.id;
      }
    }
    if (this.idInput && v) this.idInput.value = v.id;

    const mto = this.tagMto || !!(v && v.mto);
    if (this.stockEl) {
      if (this.tagMto) {
        this.stockEl.textContent = this.stockEl.dataset.mtoTag || 'Made to order · 3–7 days';
        if (this.stockDot) this.stockDot.style.background = 'var(--vrt-blue)';
      } else if (v && v.mto) {
        this.stockEl.textContent = this.stockEl.dataset.mto || 'Made to order';
        if (this.stockDot) this.stockDot.style.background = 'var(--vrt-blue)';
      } else if (!v || !available) {
        this.stockEl.textContent = this.stockEl.dataset.soldout || 'Sold out';
        if (this.stockDot) this.stockDot.style.background = 'var(--vrt-muted-4)';
      } else {
        this.stockEl.textContent = this.stockEl.dataset.instock || 'In stock';
        if (this.stockDot) this.stockDot.style.background = 'var(--vrt-green)';
      }
    }
    if (this.shipsEl) this.shipsEl.hidden = mto;

    // Reflect the selected value next to each swatch option label.
    this.querySelectorAll('[data-vrt-optlabel]').forEach((el) => {
      const input = this.optionInputs.find(
        (i) => i.dataset.position === el.dataset.position && i.checked
      );
      if (input) el.textContent = input.value;
    });

    if (v) this.syncUrl(v.id);
  }

  step(delta) {
    const next = Math.max(1, (Number(this.qtyInput.value) || 1) + delta);
    this.qtyInput.value = next;
    if (this.qtyDisplay) this.qtyDisplay.textContent = next;
    if (this.addBtn) this.addBtn.dataset.quantity = next;
  }

  showMedia(mediaId) {
    if (!this.gallery) return;
    this.gallery.querySelectorAll('[data-vrt-stage]').forEach((el) => {
      el.classList.toggle('is-active', el.dataset.mediaId === mediaId);
    });
    this.gallery.querySelectorAll('[data-vrt-thumb]').forEach((el) => {
      el.classList.toggle('is-active', el.dataset.mediaId === mediaId);
    });
  }

  openZoom() {
    if (!this.zoomOverlay || !this.gallery) return;
    const active = this.gallery.querySelector('[data-vrt-stage].is-active');
    if (active) this.showZoomMedia(active.dataset.mediaId);
    this.zoomOverlay.hidden = false;
    // Trigger lives outside the overlay, so keydown (Escape/arrows) needs
    // focus moved inside it to bubble — unlike vrt-sizeguide-modal, whose
    // trigger and overlay share one custom element.
    this.zoomOverlay.querySelector('[data-vrt-zoom-close]')?.focus();
  }

  closeZoom() {
    if (!this.zoomOverlay) return;
    this.zoomOverlay.hidden = true;
  }

  showZoomMedia(mediaId) {
    if (!this.zoomOverlay || !mediaId) return;
    this.zoomOverlay.querySelectorAll('[data-vrt-zoom-img]').forEach((img) => {
      img.classList.toggle('is-active', img.dataset.mediaId === mediaId);
    });
    // Keep the main stage/thumbs in sync so they match on close.
    this.showMedia(mediaId);
  }

  stepZoom(delta) {
    if (!this.zoomOverlay) return;
    const imgs = Array.from(this.zoomOverlay.querySelectorAll('[data-vrt-zoom-img]'));
    if (imgs.length < 2) return;
    const activeIndex = imgs.findIndex((img) => img.classList.contains('is-active'));
    const next = imgs[(activeIndex + delta + imgs.length) % imgs.length];
    if (next) this.showZoomMedia(next.dataset.mediaId);
  }

  syncUrl(variantId) {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url);
    } catch (e) {
      /* no-op */
    }
  }
}

if (!customElements.get('vrt-product-form')) {
  customElements.define('vrt-product-form', VrtProductForm);
}
